<template>
  <v-content class="client eligibilty-analytics page">
    <v-progress-linear
      v-show="loading"
      indeterminate
      :color="themes.client.light.input"
      height="5"
      class="ma-0"
    />

    <v-container>
      <v-card v-if="opportunity">
        <v-toolbar flat color="transparent">
          <v-toolbar-title>
            <div class="title">Candidate Analytics Explorer</div>
            <div class="caption grey--text text--darken-2" v-if="loading">Updating…</div>
            <div class="caption grey--text text--darken-2" v-else @click="refresh">
              <v-tooltip bottom>
                <span slot="activator">Last updated at {{ new Date(timestamp) | date("HH:mm 'on' d MMM yyyy") }}</span>
                <span>Click to refresh</span>
              </v-tooltip>
            </div>
          </v-toolbar-title>
        </v-toolbar>
      </v-card>
    </v-container>

    <v-container>
      <v-layout row wrap>
        <!-- Currently applied filters -->
        <v-flex xs6>
          <div class="title pt-2">Filters</div>
        </v-flex>
        <v-flex xs6 class="text-xs-right" v-if="!filtersApplied">
          <div class="font-weight-bold caption">No filters applied</div>
          <div class="caption">Click charts to filter</div>
        </v-flex>
        <v-flex xs12>
          <div v-if="filters.gender">
            <v-chip v-for="gender in filters.gender" :key="`gender-${gender}`"
              small close @input="toggleFilter('gender', gender)">
              <v-avatar><v-icon small>mdi-gender-male-female</v-icon></v-avatar>
              {{ gender }}
            </v-chip>
          </div>
          <div v-if="filters.ethnicity">
            <v-chip v-for="ethnicity in filters.ethnicity" :key="`ethnicity-${ethnicity}`"
              small close @input="toggleFilter('ethnicity', ethnicity)">
              <v-avatar><v-icon small>mdi-account-multiple</v-icon></v-avatar>
              {{ ethnicity }}
            </v-chip>
          </div>
          <div v-if="filters.country">
            <v-chip v-for="code in filters.country" :key="`country-${code}`"
              small close @input="toggleFilter('country', code)">
              <v-avatar><v-icon small>mdi-flag</v-icon></v-avatar>
              {{ countries.find(c => c.code == code).name }}
            </v-chip>
          </div>
          <div v-if="filters.year">
            <v-chip v-for="year in filters.year" :key="`year-${year}`"
              small close @input="toggleFilter('year', Number(year))">
              <v-avatar><v-icon small>mdi-calendar</v-icon></v-avatar>
              {{ year }}
            </v-chip>
          </div>
          <div v-if="filters.institution && institutions.length">
            <v-chip v-for="id in filters.institution" :key="`institution-${id}`"
              small close @input="toggleFilter('institution', id)">
              <v-avatar><v-icon small>mdi-bank</v-icon></v-avatar>
              {{ institutions.find(i => i.id == id).name }}
            </v-chip>
          </div>
          <div v-if="filters.qualificationType && qualificationTypes.length">
            <v-chip v-for="id in filters.qualificationType" :key="`qualification-type-${id}`"
              small close @input="toggleFilter('qualificationType', id)">
              <v-avatar><v-icon small>mdi-school</v-icon></v-avatar>
              {{ $store.getters['qualificationTypes/find'](id).name }}
            </v-chip>
          </div>
          <div v-if="filters.qualification">
            <v-chip v-for="description in filters.qualification" :key="`qualification-${description}`"
              small close @input="toggleFilter('qualification', description)">
              <v-avatar><v-icon small>mdi-decagram</v-icon></v-avatar>
              {{ description }}
            </v-chip>
          </div>
        </v-flex>
      </v-layout>
    </v-container>

    <!-- All data filtered out -->
    <v-container v-if="analytics.length > 0 && filteredAnalytics.length == 0">
      <v-alert color="warning" :value="true" icon="mdi-tray-alert">
        Your filter criteria has eliminated all applications.<br>
        Please relax the conditions until some applications match to see the
        relevant analytics.
      </v-alert>
    </v-container>

    <v-container grid-list-xl v-if="filteredAnalytics.length > 0">
      <v-layout row wrap>
        <!-- Gender -->
        <v-flex xs12 lg6>
          <v-card><v-card-text>
            <apex
              type="donut"
              width="100%"
              height="400px"
              :options="{
                title: { text: 'Genders', style: { fontSize: '24px' } },
                labels: Object.keys(genderCounts),
                colors,
                chart: {
                  fontFamily: 'Roboto, sans-serif',
                  events: {
                    dataPointSelection: genderSelected,
                  }
                },
                plotOptions: { pie: { donut: { labels: { show: true, total: { show: true } } } } },
                tooltip: { enabled: false },
              }"
              :series="Object.values(genderCounts)"
            />
          </v-card-text></v-card>
        </v-flex>

        <!-- Ethnicity -->
        <v-flex xs12 lg6>
          <v-card><v-card-text>
            <apex
              type="donut"
              width="100%"
              height="400px"
              :options="{
                title: { text: 'Ethnicities', style: { fontSize: '24px' } },
                labels: Object.keys(ethnicityCounts),
                colors,
                chart: {
                  fontFamily: 'Roboto, sans-serif',
                  events: {
                    dataPointSelection: ethnicitySelected,
                  }
                },
                plotOptions: { pie: { donut: { labels: { show: true, total: { show: true } } } } },
                tooltip: { enabled: false },
              }"
              :series="Object.values(ethnicityCounts)"
            />
          </v-card-text></v-card>
        </v-flex>

        <!-- Country -->
        <v-flex xs12 lg6 v-if="countryCounts.length">
          <v-card><v-card-text>
            <apex
              type="donut"
              width="100%"
              height="400px"
              :options="{
                title: { text: 'Country', style: { fontSize: '24px' } },
                labels: countryCounts.map(c => c[1]),
                colors,
                chart: {
                  fontFamily: 'Roboto, sans-serif',
                  events: {
                    dataPointSelection: countrySelected,
                  }
                },
                plotOptions: { pie: { donut: { labels: { show: true, total: { show: true } } } } },
                tooltip: { enabled: false },
              }"
              :series="countryCounts.map(c => c[2])"
            />
          </v-card-text></v-card>
        </v-flex>

        <!-- Graduation -->
        <v-flex xs12 lg6>
          <v-card><v-card-text>
            <apex
              type="donut"
              width="100%"
              height="400px"
              :options="{
                title: { text: 'Graduation Year', style: { fontSize: '24px' } },
                labels: Object.keys(yearCounts),
                colors,
                chart: {
                  fontFamily: 'Roboto, sans-serif',
                  events: {
                    dataPointSelection: yearSelected,
                  }
                },
                plotOptions: { pie: { donut: { labels: { show: true, total: { show: true } } } } },
                tooltip: { enabled: false },
              }"
              :series="Object.values(yearCounts)"
            />
          </v-card-text></v-card>
        </v-flex>

        <!-- Institutions -->
        <v-flex xs12 v-if="institutions.length">
          <v-card><v-card-text :class="{ 'pb-0' : hiddenInstitutionCount > 0 }">
            <apex
              type="bar"
              width="100%"
              :height="linearChartSize(topInstitutionCounts.length)"
              :options="{
                title: { text: 'Top Institutions', style: { fontSize: '24px' } },
                labels: topInstitutionCounts.map(o => o[0][1]),
                colors,
                chart: {
                  fontFamily: 'Roboto, sans-serif',
                  events: {
                    dataPointSelection: institutionSelected,
                  }
                },
                plotOptions: {
                  bar: {
                    horizontal: true,
                    dataLabels: { position: 'top' }
                  },
                },
                dataLabels: {
                  enabled: true,
                  style: { colors: ['#666'] },
                  offsetX: 24,
                },
                tooltip: { enabled: false },
                xaxis: {
                  labels: { show: false },
                  axisTicks: { show: false },
                },
                yaxis: {
                  labels: { minWidth: 200, maxWidth: 300 },
                }
              }"
              :series="[{ data: topInstitutionCounts.map(o => o[1]) }]"
            />
          </v-card-text><v-card-text v-if="hiddenInstitutionCount" class="text-xs-center caption pt-0 pb-2">
            <v-icon small>mdi-information-variant</v-icon>
            {{ hiddenInstitutionCount }} items have been omitted
          </v-card-text></v-card>
        </v-flex>

        <!-- Qualification Types -->
        <v-flex xs12 v-if="qualificationTypes.length">
          <v-card><v-card-text :class="{ 'pb-0' : hiddenQualificationTypeCount > 0 }">
            <apex
              type="bar"
              width="100%"
              :height="linearChartSize(topQualificationTypeCounts.length)"
              :options="{
                title: { text: 'Top Qualification Types', style: { fontSize: '24px' } },
                labels: topQualificationTypeCounts.map(o => o[0][1]),
                colors,
                chart: {
                  fontFamily: 'Roboto, sans-serif',
                  events: {
                    dataPointSelection: qualificationTypeSelected,
                  }
                },
                plotOptions: {
                  bar: {
                    horizontal: true,
                    dataLabels: { position: 'top' }
                  },
                },
                dataLabels: {
                  enabled: true,
                  style: { colors: ['#666'] },
                  offsetX: 24,
                },
                tooltip: { enabled: false },
                xaxis: {
                  labels: { show: false },
                  axisTicks: { show: false },
                },
                yaxis: {
                  labels: { minWidth: 200, maxWidth: 300 },
                }
              }"
              :series="[{ data: topQualificationTypeCounts.map(o => o[1]) }]"
            />
          </v-card-text><v-card-text v-if="hiddenQualificationTypeCount" class="text-xs-center caption pt-0 pb-2">
            <v-icon small>mdi-information-variant</v-icon>
            {{ hiddenQualificationTypeCount }} items have been omitted
          </v-card-text></v-card>
        </v-flex>

        <!-- Qualifications -->
        <v-flex xs12 v-if="analytics.length">
          <v-card><v-card-text :class="{ 'pb-0' : hiddenQualificationCount > 0 }">
            <apex
              type="bar"
              width="100%"
              :height="linearChartSize(topQualificationCounts.length)"
              :options="{
                title: { text: 'Top Qualifications', style: { fontSize: '24px' } },
                labels: topQualificationCounts.map(o => o[0]),
                colors,
                chart: {
                  fontFamily: 'Roboto, sans-serif',
                  events: {
                    dataPointSelection: qualificationSelected,
                  }
                },
                plotOptions: {
                  bar: {
                    horizontal: true,
                    dataLabels: { position: 'top' }
                  },
                },
                dataLabels: {
                  enabled: true,
                  style: { colors: ['#666'] },
                  offsetX: 24,
                },
                tooltip: { enabled: false },
                xaxis: {
                  labels: { show: false },
                  axisTicks: { show: false },
                },
                yaxis: {
                  labels: { minWidth: 200, maxWidth: 300 },
                }
              }"
              :series="[{ data: topQualificationCounts.map(o => o[1]) }]"
            />
          </v-card-text><v-card-text v-if="hiddenQualificationCount" class="text-xs-center caption pt-0 pb-2">
            <v-icon small>mdi-information-variant</v-icon>
            {{ hiddenQualificationCount }} items have been omitted
          </v-card-text></v-card>
        </v-flex>
      </v-layout>
    </v-container>

  </v-content>
</template>

<script>
// Mixins
import { managedDocumentAttributes } from '@/mixins/managedDocumentAttributes.js'
import { managedQueryParameters } from '@/mixins/managedQueryParameters.js'

// Data
import colors from '@/data/colors'
import countries from '@/data/countries'

/* Eligibility Structure */
// [
//   id,                            // 0
//   country_of_birth,              // 1
//   ethnicity,                     // 2
//   gender,                        // 3
//   institution_id,                // 4
//   qualification_type_id,         // 5
//   qualification_description,     // 6
//   profile_qualification_ends_at, // 7
// ]

// Vue Component
export default {
  mixins: [
    managedDocumentAttributes,
    managedQueryParameters
  ],

  created() {
    this.initializeFilters()
    this.$store.dispatch('qualificationTypes/fetchAll')
    this.$store.dispatch('institutions/fetchAll')
    this.refresh()
  },
  data() {
    return {
      loading: false,
      timestamp: null,
      analytics: [],
      colors: colors.analytics,
      countries,
      // Filter interface
      showfilters: false,
      filterSelection: null,
      filters: {
        gender: [],
        ethnicity: [],
        institution: [],
        qualificationType: [],
        qualification: [],
        year: [],
        country: [],
      }
    }
  },
  computed: {
    // Legend data
    allGenders() { return [...new Set(this.analytics.map(a => a[3]))].sort() },
    allEthnicities() { return [...new Set(this.analytics.map(a => a[2]))].sort() },
    allCountries() { return [...new Set(this.analytics.map(a => a[1]))].sort() },
    allYears() { return [...new Set(this.analytics.map(a => a[7]))].sort() },

    opportunity() {
      return this.$store.getters['client/opportunities/find']()
    },

    institutions() {
      return this.$store.getters['institutions/all']
    },
    qualificationTypes() {
      return this.$store.getters['qualificationTypes/all']
    },

    filteredAnalytics() {
      return this.analytics.filter(e => {
        return (!this.filters.gender.length || this.filters.gender.includes(e[3])) &&
               (!this.filters.ethnicity.length || this.filters.ethnicity.includes(e[2])) &&
               (!this.filters.country.length || this.filters.country.includes(e[1])) &&
               (!this.filters.year.length || this.filters.year.includes(Number(e[7]))) &&
               (!this.filters.institution.length || this.filters.institution.includes(e[4])) &&
               (!this.filters.qualificationType.length || this.filters.qualificationType.includes(e[5])) &&
               (!this.filters.qualification.length || this.filters.qualification.includes(e[6]))
      })
    },

    filtersApplied() {
      let result = false
      Object.values(this.filters).forEach(values => {
        if (values.length) { result = true }
      })
      return result
    },

    genderCounts() {
      let counts = {}
      this.filteredAnalytics.forEach(eligibility => {
        let gender = eligibility[3]
        if (!gender) { gender = 'Unspecified' }
        counts[gender] = (counts[gender] || 0) + 1
      })

      let result = {}

      this.allGenders.forEach(gender => {
        if (!counts[gender]) {
          result[gender] = 0
        } else {
          result[gender] = counts[gender]
        }
      })

      return result
    },
    ethnicityCounts() {
      let counts = {}
      this.filteredAnalytics.forEach(eligibility => {
        let ethnicity = eligibility[2]
        if (!ethnicity) { ethnicity = 'Unspecified' }
        counts[ethnicity] = (counts[ethnicity] || 0) + 1
      })

      let result = {}

      this.allEthnicities.forEach(ethnicity => {
        if (!counts[ethnicity]) {
          result[ethnicity] = 0
        } else {
          result[ethnicity] = counts[ethnicity]
        }
      })

      return result
    },
    countryCounts() {
      let counts = {}
      this.filteredAnalytics.forEach(eligibility => {
        let code = eligibility[1]
        if (!code) { code = 'XX' }
        counts[code] = (counts[code] || 0) + 1
      })

      let result = []

      this.allCountries.forEach(code => {
        let country = countries.find(c => c.code == code)
        let name = country ? country.name : 'Unspecified'
        result.push([code, name, counts[code] ? counts[code] : 0])
      })

      return result
    },
    yearCounts() {
      let counts = {}
      this.filteredAnalytics.forEach(eligibility => {
        let year = eligibility[7]
        if (!year) { year = 'Unspecified' }
        counts[year] = (counts[year] || 0) + 1
      })

      let result = {}

      this.allYears.forEach(year => {
        if (!counts[year]) {
          result[year] = 0
        } else {
          result[year] = counts[year]
        }
      })

      return result
    },

    institutionCounts() {
      let counts = {}
      this.filteredAnalytics.forEach(eligibility => {
        let institution = eligibility[4]
        if (institution) {
          counts[institution] = (counts[institution] || 0) + 1
        }
      })

      let sorted = []
      for (let id in counts) {
        let institution = this.institutions.find(i => i.id == id)
        sorted.push([
          [Number(id), institution.name],
          counts[id]
        ])
      }
      sorted.sort((a, b) => b[1] - a[1])
      return sorted
    },
    topInstitutionCounts() {
      const desired = 20
      if (this.institutionCounts.length < desired) {
        return this.institutionCounts
      } else {
        let top = []
        for (var i = 0; i < this.institutionCounts.length; i++) {
          if (top.length < desired) {
            top.push(this.institutionCounts[i])
          } else {
            break // for
          }
        }
        return top
      }
    },
    hiddenInstitutionCount() {
      if (this.institutionCounts.length === this.topInstitutionCounts.count) {
        return 0
      } else {
        const tops = _.sum(this.topInstitutionCounts.map(([n, c]) => c))
        return this.filteredAnalytics.length - tops
      }
    },

    // Qualification Types
    qualificationTypeCounts() {
      let counts = {}
      this.filteredAnalytics.forEach(eligibility => {
        let qualification = eligibility[5]
        if (qualification) {
          counts[qualification] = (counts[qualification] || 0) + 1
        }
      })

      let find = this.$store.getters['qualificationTypes/find']
      let sorted = []
      for (let qualification in counts) {
        let qual = find(qualification)
        sorted.push([
          [Number(qualification), (qual ? qual.name : 'Missing qualification')],
          counts[qualification]
        ])
      }
      sorted.sort((a, b) => b[1] - a[1])
      return sorted
    },
    topQualificationTypeCounts() {
      const desired = 20
      if (this.qualificationTypeCounts.length < desired) {
        return this.qualificationTypeCounts
      } else {
        let top = []
        for (var i = 0; i < this.qualificationTypeCounts.length; i++) {
          if (top.length < desired) {
            top.push(this.qualificationTypeCounts[i])
          } else {
            break // for
          }
        }
        return top
      }
    },
    hiddenQualificationTypeCount() {
      if (this.qualificationTypeCounts.length === this.topQualificationTypeCounts.count) {
        return 0
      } else {
        const tops = _.sum(this.topQualificationTypeCounts.map(([n, c]) => c))
        return this.filteredAnalytics.length - tops
      }
    },

    // Qualifications
    qualificationCounts() {
      let counts = {}
      this.filteredAnalytics.forEach(eligibility => {
        let qualification = eligibility[6]
        if (qualification) {
          counts[qualification] = (counts[qualification] || 0) + 1
        }
      })

      let sorted = []
      for (let qualification in counts) {
        sorted.push([
          qualification,
          counts[qualification]
        ])
      }
      sorted.sort((a, b) => b[1] - a[1])
      return sorted
    },
    topQualificationCounts() {
      const desired = 20
      if (this.qualificationCounts.length < desired) {
        return this.qualificationCounts
      } else {
        let top = []
        for (var i = 0; i < this.qualificationCounts.length; i++) {
          if (top.length < desired) {
            top.push(this.qualificationCounts[i])
          } else {
            break // for
          }
        }
        return top
      }
    },
    hiddenQualificationCount() {
      if (this.qualificationCounts.length === this.topQualificationCounts.count) {
        return 0
      } else {
        const tops = _.sum(this.topQualificationCounts.map(([n, c]) => c))
        return this.filteredAnalytics.length - tops
      }
    },
  },
  methods: {
    refresh() {
      this.loading = true
      this.$store.dispatch('opportunities/fetch', this.$route.params.id).then(() => {
        this.documentAttributes.title = 'Candidate Analytics Explorer - Leaply'
      })
      this.$http.get(`client/opportunities/${this.$route.params.id}/analytics/eligibilities`).then(response => {
        this.analytics = response.data
        this.timestamp = new Date()
        this.loading = false
      })
    },

    initializeFilters() {
      Object.keys(this.filters).forEach(f => {
        // No value
        if (!this.initialQueryParameters[f]) {
          return
        }

        // Filter type is array and query param is array
        if (typeof this.initialQueryParameters[f] === 'object') {
          this.filters[f] = this.initialQueryParameters[f].map(param => {
            if (f === 'qualificationType' || f === 'institution' || f === 'year') {
              return Number(param)
            } else {
              return param
            }
          })
          return
        }

        // Other
        this.filters[f] = this.initialQueryParameters[f]
      })
    },
    toggleFilter(filter, value) {
      // Boolean filter
      if ([null, true, false].indexOf(value) > -1) {
        this.filters[filter] = value
      } else if (this.filters[filter].indexOf(value) > -1) {
        // Exists, toggle off
        this.filters[filter].splice(this.filters[filter].indexOf(value), 1)
      } else {
        // Doesn't exist, toggle on
        this.filters[filter].push(value)
      }
      this.updateQueryParameters(this.filters)
    },

    linearChartSize(count) {
      return 100 + count * 20
    },

    genderSelected(event, chartContext, config) {
      const { dataPointIndex } = config
      const gender = Object.keys(this.genderCounts)[dataPointIndex]
      this.toggleFilter('gender', gender)
    },

    ethnicitySelected(event, chartContext, config) {
      const { dataPointIndex } = config
      const ethnicity = Object.keys(this.ethnicityCounts)[dataPointIndex]
      this.toggleFilter('ethnicity', ethnicity)
    },

    countrySelected(event, chartContext, config) {
      const { dataPointIndex } = config
      const countryCode = this.countryCounts[dataPointIndex][0]
      this.toggleFilter('country', countryCode)
    },

    yearSelected(event, chartContext, config) {
      const { dataPointIndex } = config
      const year = Object.keys(this.yearCounts)[dataPointIndex]
      this.toggleFilter('year', Number(year))
    },

    institutionSelected(event, chartContext, config) {
      const { dataPointIndex } = config
      const institutionItem = this.topInstitutionCounts[dataPointIndex]
      this.toggleFilter('institution', institutionItem[0][0])
    },

    qualificationTypeSelected(event, chartContext, config) {
      const { dataPointIndex } = config
      const qualificationTypeItem = this.topQualificationTypeCounts[dataPointIndex]
      this.toggleFilter('qualificationType', qualificationTypeItem[0][0])
    },

    qualificationSelected(event, chartContext, config) {
      const { dataPointIndex } = config
      const qualificationItem = this.topQualificationCounts[dataPointIndex]
      this.toggleFilter('qualification', qualificationItem[0])
    },
  },
}
</script>
